/** E10: Warteliste, Aufgaben, Schichtplan, Schwund, Rechte je Person – Hooks für die Verwaltung. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, rufe } from "../api/client";
import type { aufgabenListe, schichtenWoche, schwundUebersicht, wartelisteListe } from "../../worker/data/laden-alltag";
import type { Recht } from "@shared/schemas/basis";

export type WartelisteEintrag = Awaited<ReturnType<typeof wartelisteListe>>[number];
export type Aufgabe = Awaited<ReturnType<typeof aufgabenListe>>[number];
export type Schichtwoche = Awaited<ReturnType<typeof schichtenWoche>>;
export type Schwund = Awaited<ReturnType<typeof schwundUebersicht>>;

function nach() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ["laden"] });
}

export const useWarteliste = () => useQuery({ queryKey: ["laden", "warteliste"], queryFn: () => rufe<{ eintraege: WartelisteEintrag[] }>(api.api.laden.warteliste.$get()) });
export function useWartelisteAktion() {
  const n = nach();
  return useMutation({
    mutationFn: (v: { art: "anlegen"; daten: { name: string; email: string; telefon: string; wunsch: string; abTag: string | null; notiz: string } } | { art: "status"; id: string; status: "offen" | "erledigt"; notiz?: string } | { art: "umwandeln"; id: string } | { art: "informieren" }) => {
      if (v.art === "anlegen") return rufe<{ id: string; position: number }>(api.api.laden.warteliste.$post({ json: v.daten }));
      if (v.art === "status") return rufe(api.api.laden.warteliste[":id"].status.$post({ param: { id: v.id }, json: { status: v.status, notiz: v.notiz } }));
      if (v.art === "umwandeln") return rufe<{ mieterId: string; mieternummer: string }>(api.api.laden.warteliste[":id"].umwandeln.$post({ param: { id: v.id } }));
      return rufe<{ informiert: number }>(api.api.laden.warteliste.informieren.$post());
    },
    onSuccess: n,
  });
}

export const useAufgaben = (nur: "offen" | "erledigt" | "alle" = "offen") => useQuery({ queryKey: ["laden", "aufgaben", nur], queryFn: () => rufe<{ aufgaben: Aufgabe[] }>(api.api.laden.aufgaben.$get({ query: { nur } })) });
export function useAufgabenAktion() {
  const n = nach();
  return useMutation({
    mutationFn: (v: { art: "anlegen"; daten: { titel: string; text: string; zugewiesenBenutzerId: string | null; faelligTag: string | null } } | { art: "aendern"; id: string; daten: { erledigt?: boolean; zugewiesenBenutzerId?: string | null; faelligTag?: string | null } } | { art: "automatik" }) => {
      if (v.art === "anlegen") return rufe<{ id: string | null }>(api.api.laden.aufgaben.$post({ json: v.daten }));
      if (v.art === "aendern") return rufe(api.api.laden.aufgaben[":id"].$put({ param: { id: v.id }, json: v.daten }));
      return rufe<{ neu: number }>(api.api.laden.aufgaben.automatik.$post());
    },
    onSuccess: n,
  });
}

export const useSchichten = (von: string) => useQuery({ queryKey: ["laden", "schichten", von], queryFn: () => rufe<Schichtwoche>(api.api.laden.schichten.$get({ query: { von } })), placeholderData: (alt) => alt });
export function useSchichtAktion() {
  const n = nach();
  return useMutation({
    mutationFn: (v: { art: "anlegen"; daten: { benutzerId: string; tag: string; von: string; bis: string; art: "schicht" | "abwesend"; notiz: string } } | { art: "loeschen"; id: string }) =>
      v.art === "anlegen" ? rufe<{ id: string }>(api.api.laden.schichten.$post({ json: v.daten })) : rufe(api.api.laden.schichten[":id"].$delete({ param: { id: v.id } })),
    onSuccess: n,
  });
}

export const useSchwund = () => useQuery({ queryKey: ["laden", "schwund"], queryFn: () => rufe<Schwund>(api.api.laden.schwund.$get()) });
export function useSchwundMelden() {
  const n = nach();
  return useMutation({ mutationFn: (v: { artikelId: string; grund: string }) => rufe(api.api.laden.artikel[":id"].schwund.$post({ param: { id: v.artikelId }, json: { grund: v.grund } })), onSuccess: n });
}

export const useFeiertage = (jahr: number) => useQuery({ queryKey: ["laden", "feiertage", jahr], queryFn: () => rufe<{ feiertage: { tag: string; name: string }[] }>(api.api.laden.feiertage[":jahr"].$get({ param: { jahr: String(jahr) } })), staleTime: Infinity });

export const useRechte = (benutzerId: string) => useQuery({ queryKey: ["laden", "personal", benutzerId, "rechte"], queryFn: () => rufe<{ rolle: string; ueberschreibungen: Record<string, boolean> }>(api.api.laden.personal[":benutzerId"].rechte.$get({ param: { benutzerId } })), enabled: !!benutzerId });
export function useRechteSpeichern() {
  const n = nach();
  return useMutation({ mutationFn: (v: { benutzerId: string; rechte: Partial<Record<Recht, boolean>> }) => rufe(api.api.laden.personal[":benutzerId"].rechte.$put({ param: { benutzerId: v.benutzerId }, json: { rechte: v.rechte as Record<string, boolean> } })), onSuccess: n });
}
